import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, Github, Loader2, Lock, Plus, Search, X } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/** Création d'un projet NovaForge, avec un dépôt GitHub créé ou relié. */
export default function NouveauProjet({ token, onFerme }) {
  const navigate = useNavigate();
  const [f, setF] = useState({ name: "", company: "", pour: "client", opportunity_id: null, client_contact: "",
                               client_email: "", description: "", github_mode: "creer",
                               github_depot: "", github_prive: true, cloner: true });
  const [envoi, setEnvoi] = useState(false);
  const [github, setGithub] = useState(null);
  const [recherche, setRecherche] = useState("");
  const [depots, setDepots] = useState(null);
  const [societes, setSocietes] = useState([]);
  const [clients, setClients] = useState([]);
  const maj = (k, v) => setF((o) => ({ ...o, [k]: v }));
  const h = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetch(`${API_URL}/api/agent/github/etat`, { headers: h }).then((r) => r.json()).then(setGithub).catch(() => {});
    fetch(`${API_URL}/api/agent/projets/clients`, { headers: h }).then((r) => r.json())
      .then((l) => setClients(Array.isArray(l) ? l : [])).catch(() => {});
    fetch(`${API_URL}/api/projects`, { headers: h }).then((r) => r.json())
      .then((l) => setSocietes([...new Set((Array.isArray(l) ? l : []).map((p) => p.company).filter(Boolean))].sort()))
      .catch(() => {});
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (f.github_mode !== "existant") return;
    const t = setTimeout(() => {
      setDepots(null);
      fetch(`${API_URL}/api/agent/github/depots?q=${encodeURIComponent(recherche)}`, { headers: h })
        .then((r) => r.json()).then(setDepots).catch(() => setDepots({ depots: [], note: "GitHub injoignable" }));
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [recherche, f.github_mode]);

  const creer = async () => {
    if (!f.name.trim()) return toast.error("Donne un nom au projet");
    setEnvoi(true);
    try {
      const r = await fetch(`${API_URL}/api/agent/projets`, {
        method: "POST", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(f),
      });
      const d = await r.json();
      if (!r.ok && d.detail?.projet_id) {
        toast.error(d.detail.message, { action: { label: "Ouvrir", onClick: () => navigate(`/dashboard/projects/${d.detail.projet_id}`) } });
        return;
      }
      if (!r.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Création impossible");
      toast.success(`Projet « ${d.projet.name} » créé${d.projet.github_url ? " et relié à GitHub" : ""}`);
      navigate(`/dashboard/projects/${d.projet.id}/activite`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setEnvoi(false);
    }
  };

  const champ = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm";
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/30" onClick={onFerme}>
      <aside className="h-full w-full max-w-lg overflow-y-auto bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold">Nouveau projet</h2>
          <button onClick={onFerme} className="rounded-lg p-1.5 hover:bg-slate-100"><X className="h-4 w-4" /></button>
        </div>
        <div className="mt-5 space-y-3">
          <input className={champ} placeholder="Nom du projet" value={f.name} onChange={(e) => maj("name", e.target.value)} />
          <div className="inline-flex rounded-xl border border-slate-200 bg-slate-100 p-1">
            {[["client", "Pour un client"], ["interne", "Interne au groupe"]].map(([k, l]) => (
              <button key={k} onClick={() => setF((o) => ({ ...o, pour: k, company: "", opportunity_id: null }))}
                className={`rounded-lg px-3 py-1.5 text-sm ${f.pour === k ? "bg-white font-semibold text-indigo-700 shadow-sm" : "text-slate-500"}`}>{l}</button>
            ))}
          </div>
          {f.pour === "client" ? (
            <>
              <input className={champ} list="clients-projets" placeholder="Client (existant du CRM ou nouveau)"
                value={f.company}
                onChange={(e) => {
                  const nom = e.target.value;
                  const existant = clients.find((c) => c.company_name.toLowerCase() === nom.trim().toLowerCase());
                  setF((o) => ({ ...o, company: nom, opportunity_id: existant ? existant.id : null }));
                }} />
              <datalist id="clients-projets">{clients.map((c) => <option key={c.id} value={c.company_name}>{c.contact_name}</option>)}</datalist>
              {f.company.trim() && (f.opportunity_id ? (
                <p className="text-xs text-emerald-700">Client existant du CRM : le projet lui sera rattaché.</p>
              ) : (
                <div className="grid gap-2 sm:grid-cols-2">
                  <p className="text-xs text-slate-500 sm:col-span-2">Nouveau client : il sera ajouté aux Clients.</p>
                  <input className={champ} placeholder="Contact (facultatif)" value={f.client_contact} onChange={(e) => maj("client_contact", e.target.value)} />
                  <input className={champ} placeholder="Email (facultatif)" value={f.client_email} onChange={(e) => maj("client_email", e.target.value)} />
                </div>
              ))}
            </>
          ) : (
            <>
              <input className={champ} list="societes-projets" placeholder="Société du groupe (ex. RM Location)" value={f.company} onChange={(e) => maj("company", e.target.value)} />
              <datalist id="societes-projets">{societes.map((x) => <option key={x} value={x} />)}</datalist>
            </>
          )}
          <textarea className={champ} rows={3} placeholder="En quelques lignes : à quoi sert l'application" value={f.description}
            onChange={(e) => maj("description", e.target.value)} />
          <div>
            <p className="flex items-center gap-1.5 text-sm font-semibold text-slate-700"><Github className="h-4 w-4" /> Dépôt GitHub</p>
            <div className="mt-2 inline-flex rounded-xl border border-slate-200 bg-slate-100 p-1">
              {[["creer", "Créer"], ["existant", "Relier un existant"], ["aucun", "Aucun"]].map(([k, l]) => (
                <button key={k} onClick={() => maj("github_mode", k)}
                  className={`rounded-lg px-3 py-1.5 text-sm ${f.github_mode === k ? "bg-white font-semibold text-indigo-700 shadow-sm" : "text-slate-500"}`}>{l}</button>
              ))}
            </div>
          </div>
          {github && f.github_mode === "creer" && !github.jeton_valide && (
            <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                {github.erreur || "Aucun jeton GitHub : la création de dépôt est impossible pour l'instant."} Ajoute GITHUB_TOKEN dans{" "}
                <a href="/dashboard/infrastructure" className="font-semibold underline">Infrastructure › Secrets</a>{" "}
                (jeton « fine-grained », droit Administration en lecture et écriture). En attendant, crée le dépôt sur
                GitHub puis choisis « Relier un existant ».
              </span>
            </div>
          )}
          {github?.jeton_valide && f.github_mode === "creer" && (
            <p className="flex items-center gap-1.5 text-xs text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> Jeton GitHub valide ({github.compte})</p>
          )}
          {f.github_mode === "existant" && (
            <div className="rounded-lg border border-slate-200">
              <div className="flex items-center gap-2 border-b border-slate-200 px-3 py-2">
                <Search className="h-4 w-4 text-slate-400" />
                <input value={recherche} onChange={(e) => setRecherche(e.target.value)} placeholder="Chercher un dépôt…"
                  className="flex-1 text-sm outline-none" />
              </div>
              <div className="max-h-60 overflow-y-auto">
                {!depots ? <Loader2 className="m-3 h-4 w-4 animate-spin text-slate-400" /> : depots.depots.map((d) => (
                  <button key={d.nom} disabled={!!d.projet}
                    onClick={() => maj("github_depot", d.nom)}
                    className={`flex w-full items-start gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 ${f.github_depot === d.nom ? "bg-indigo-50" : ""}`}>
                    {d.prive ? <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" /> : <Github className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />}
                    <span className="min-w-0 flex-1">
                      <span className="font-medium text-slate-800">{d.nom}</span>
                      {d.description && <span className="block truncate text-xs text-slate-500">{d.description}</span>}
                    </span>
                    <span className="shrink-0 text-xs text-slate-400">{d.projet ? `déjà « ${d.projet.name} »` : d.maj}</span>
                  </button>
                ))}
              </div>
              {depots?.note && <p className="border-t border-slate-200 px-3 py-2 text-xs text-slate-500">{depots.note}</p>}
            </div>
          )}
          {f.github_mode !== "aucun" && (
            <>
              <input className={champ} value={f.github_depot} onChange={(e) => maj("github_depot", e.target.value)}
                placeholder={f.github_mode === "creer" ? "Nom du dépôt (sinon tiré du nom du projet)" : "URL ou RunOdoo/nom-du-depot"} />
              {f.github_mode === "creer" && (
                <label className="flex items-center gap-2 text-sm text-slate-600">
                  <input type="checkbox" checked={f.github_prive} onChange={(e) => maj("github_prive", e.target.checked)} /> Dépôt privé
                </label>
              )}
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <input type="checkbox" checked={f.cloner} onChange={(e) => maj("cloner", e.target.checked)} />
                Cloner sur le serveur (dossier de travail des agents, dans /opt/apps/dev)
              </label>
            </>
          )}
          <p className="text-xs text-slate-500">
            Un lien de reprise est créé avec le projet : colle-le dans Codex, Orca ou un autre outil pour reprendre
            le travail avec la mémoire et le fil des échanges.
          </p>
          <button onClick={creer} disabled={envoi}
            className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
            {envoi ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Créer le projet
          </button>
        </div>
      </aside>
    </div>
  );
}
